import * as React from "react";

import { Drawer as DrawerPrimitive } from "vaul";

import { cn } from "~/lib/utils";

const Drawer = ({
    shouldScaleBackground = true,
    ...props
}: React.ComponentProps<typeof DrawerPrimitive.Root>) => (
    <DrawerPrimitive.Root
        shouldScaleBackground={shouldScaleBackground}
        {...props}
    />
);
Drawer.displayName = "Drawer";

const DrawerTrigger = DrawerPrimitive.Trigger;

const DrawerPortal = DrawerPrimitive.Portal;

const DrawerClose = DrawerPrimitive.Close;

const DrawerOverlay = React.forwardRef<
    React.ElementRef<typeof DrawerPrimitive.Overlay>,
    React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay>
>(({ className, ...props }, ref) => (
    <DrawerPrimitive.Overlay
        ref={ref}
        className={cn("fixed inset-0 z-50 bg-black/20", className)}
        {...props}
    />
));
DrawerOverlay.displayName = DrawerPrimitive.Overlay.displayName;

const DrawerContent = React.forwardRef<
    React.ElementRef<typeof DrawerPrimitive.Content>,
    React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Content>
>(({ className, children, ...props }, ref) => (
    <DrawerPortal>
        <DrawerOverlay />
        <DrawerPrimitive.Content
            ref={ref}
            className={cn(
                "fixed bottom-0 z-50 mt-24 max-h-[100vh] flex h-auto flex-col rounded-t-[10px] border bg-background ",
                `right-0`,
                className,
            )}
            {...props}
        >
            {/* AIDEV-NOTE: relative z-10 keeps the grab handle visible when a
                DrawerHeader masthead bleeds up underneath it (see DrawerHeader). */}
            <div className="relative z-10 mx-auto mt-4 h-2 w-[100px] rounded-full bg-muted" />
            {children}
        </DrawerPrimitive.Content>
    </DrawerPortal>
));
DrawerContent.displayName = "DrawerContent";

// AIDEV-NOTE: WHITE MASTHEAD (design refresh Sep 2026, matching DialogHeader) — the
// header bleeds up over the grab-handle strip (-mt-6 = the handle's mt-4 + h-2) so the
// white band runs to the drawer's rounded top edge, then mb-4 leaves space before any
// body content. `badge` is an optional node (e.g. <Badge variant="terra">NEW</Badge>)
// rendered above the title; existing drawers pass nothing and look as before, just
// with the white band behind their title.
const DrawerHeader = ({
    className,
    badge = null,
    children,
    ...props
}: React.HTMLAttributes<HTMLDivElement> & { badge?: React.ReactNode }) => (
    <div
        className={cn(
            "-mt-6 mb-4 grid gap-1.5 rounded-t-[10px] border-b border-brand-forest-100 bg-white p-4 pt-7 text-center sm:text-left",
            className,
        )}
        {...props}
    >
        {badge ? <div className="leading-none">{badge}</div> : null}
        {children}
    </div>
);
DrawerHeader.displayName = "DrawerHeader";

const DrawerFooter = ({
    className,
    ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
    <div
        className={cn("mt-auto flex flex-col gap-2 p-4", className)}
        {...props}
    />
);
DrawerFooter.displayName = "DrawerFooter";

const DrawerTitle = React.forwardRef<
    React.ElementRef<typeof DrawerPrimitive.Title>,
    React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Title>
>(({ className, ...props }, ref) => (
    <DrawerPrimitive.Title
        ref={ref}
        // AIDEV-NOTE: Brand serif, matching DialogTitle (masthead design refresh Sep 2026).
        className={cn(
            "font-display text-xl font-semibold leading-snug tracking-tight text-brand-forest-800",
            className,
        )}
        {...props}
    />
));
DrawerTitle.displayName = DrawerPrimitive.Title.displayName;

const DrawerDescription = React.forwardRef<
    React.ElementRef<typeof DrawerPrimitive.Description>,
    React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Description>
>(({ className, ...props }, ref) => (
    <DrawerPrimitive.Description
        ref={ref}
        className={cn("text-sm text-muted-foreground", className)}
        {...props}
    />
));
DrawerDescription.displayName = DrawerPrimitive.Description.displayName;

export {
    Drawer,
    DrawerPortal,
    DrawerOverlay,
    DrawerTrigger,
    DrawerClose,
    DrawerContent,
    DrawerHeader,
    DrawerFooter,
    DrawerTitle,
    DrawerDescription,
};
